import type { ExistingPatientMatch } from '@/services/existingPatientMatch'
import { InfoCircle } from 'flowbite-react-icons/outline'

/** ``YYYY-MM-DD`` to ``MM/DD/YYYY`` without a timezone round-trip. */
function formatDob(value: string | null): string | null {
  if (!value) return null
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim())
  if (!parts) return value.trim() || null
  return `${parts[2]}/${parts[3]}/${parts[1]}`
}

function formatOrderDate(value: string | null): string | null {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

/** Lower-case so it reads as prose mid-sentence ("with a submitted order"). */
function formatStatus(status: string): string {
  return status.trim().replace(/[_-]+/g, ' ').toLowerCase()
}

/**
 * "a" or "an" for the phrase that follows.
 *
 * Statuses reach this from the server, and `accessioned` — the effective status
 * of any submitted order the lab has received — begins with a vowel. Testing
 * the letter rather than keeping a list of statuses means a status added later
 * reads correctly without anyone remembering this function exists.
 */
function indefiniteArticle(phrase: string): string {
  return /^[aeiou]/i.test(phrase.trim()) ? 'an' : 'a'
}

function patientName(match: ExistingPatientMatch): string | null {
  const name = [match.patient_first_name, match.patient_last_name].filter(Boolean).join(' ').trim()
  return name || null
}

/**
 * Builds the sentence describing the existing order.
 *
 * Every clause is dropped when the server did not supply its field, so a thin
 * match degrades to a shorter true sentence instead of rendering "undefined".
 */
export function buildExistingOrderSentence(match: ExistingPatientMatch): string {
  const name = patientName(match)
  const dob = formatDob(match.patient_date_of_birth)
  const status = formatStatus(match.status)
  const orderDate = formatOrderDate(match.created_at)

  // Name+DOB matches have no MRN to quote back, so they lead with the patient.
  const subject = match.matched_on === 'mrn' && match.patient_mrn ? match.patient_mrn : name
  const clauses: string[] = []
  if (subject) {
    clauses.push(name && subject !== name ? `${subject} is on file as ${name}` : `${subject} is on file`)
  } else {
    clauses.push('This patient is already on file')
  }
  if (dob) clauses.push(`DOB ${dob}`)

  const test = match.sequencing_name?.trim()
  const orderPhrase = [status, test].filter(Boolean).join(' ')
  const article = indefiniteArticle(orderPhrase || 'order')
  let withClause = `with ${article}${orderPhrase ? ` ${orderPhrase}` : ''} order`
  if (orderDate) withClause += ` from ${orderDate}`
  clauses.push(withClause)

  return `${clauses.join(', ')}.`
}

/**
 * Warns a provider that the identifiers they just typed already belong to a
 * patient in their organization, and offers the existing order as the only way
 * forward. There is deliberately no "create anyway": the duplicate this would
 * create is the problem the banner exists to prevent.
 */
export function ExistingOrderFoundBanner({
  match,
  onOpenExistingOrder,
}: {
  match: ExistingPatientMatch
  onOpenExistingOrder: () => void
}) {
  return (
    <div
      className="flex w-full flex-col gap-4 rounded-base border border-[var(--colors-border-border-warning-subtle,#FDBA8C)] bg-[var(--colors-background-bg-warning-soft,#FFF8F1)] p-4"
      role="alert"
      aria-live="polite"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <InfoCircle className="h-4 w-4 shrink-0 text-[var(--colors-text-text-fg-warning,#B43403)]" />
          <p className="text-lg font-semibold leading-5 tracking-tight text-[var(--colors-text-text-fg-warning,#B43403)]">
            Existing Order Found
          </p>
        </div>
        <p className="font-sans text-sm font-normal leading-5 text-[var(--colors-text-text-fg-warning,#B43403)]">
          {buildExistingOrderSentence(match)} If this is a different patient, check the MRN.
        </p>
      </div>
      <button
        type="button"
        onClick={onOpenExistingOrder}
        className="inline-flex items-center justify-center gap-1.5 self-start whitespace-nowrap rounded-full bg-[var(--colors-background-bg-warning,#FF5A1F)] px-2.5 py-1.5 text-xs font-medium text-white shadow-xs transition-colors hover:opacity-90"
      >
        Open Existing Order
      </button>
    </div>
  )
}

export default ExistingOrderFoundBanner
